import React, { forwardRef, useState } from 'react';
import { Box } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import {
  CRUD_COMPONENT_TYPE,
  CRUDComponent,
  useDataProvider,
  useLocalization,
  LoadingEvent,
  useSnackbar,
} from '@ui-framework/index';
import { nanoid } from 'nanoid';
import PermissionButton from '@product-framework/components/PermissionButton';
import {
  actions,
  client,
  industry,
  brand,
  productLine,
  model,
  serviceSite,
  serviceType,
  repairSeverity,
  repairCode,
  laborRate,
  travelRate,
  effectiveDate,
  endDate,
} from './accessorFunctions';
import listSchema from './list.json';
import LaborMatrixRateRuleEditDialog from '../LaborMatrixRateRuleEditDialog';
import LaborMatrixRateRuleDeleteDialog from '../LaborMatrixRateRuleDeleteDialog';
import rulesFormatter from '../utils/rulesFormatter';

const LaborMatrixTable = forwardRef(({ laborMatrixId }, ref) => {
  const { formatMessage } = useLocalization();
  const snackbar = useSnackbar();
  const provider =
    process.env.NODE_ENV === 'development' ? 'sxclServices' : 'default';
  const dataProvider = useDataProvider(provider);
  const [selectedEditId, setSelectedEditId] = useState(null);
  const [selectedDeleteId, setSelectedDeleteId] = useState(null);
  const [selectedPriority, setSelectedPriority] = useState(null);
  const [tableRenderId, setTableRenderId] = useState(nanoid());
  const [loading, setLoading] = useState(false);

  const listApiUrl = `${dataProvider.getApiUrl()}/sxcl/v1/labor-matrices/${laborMatrixId}/rate-rules`;
  const updateApiUrl = rateRuleId =>
    `${dataProvider.getApiUrl()}/sxcl/v1/labor-matrices/${laborMatrixId}/rate-rules/${rateRuleId}`;

  const preProcessRequestData = data => {
    const { priority } = data;
    return {
      priority,
    };
  };

  // Flatten the response to avaoid page crash
  const preProcessListResponseData = data => {
    const newData = data.map(d => {
      const newProps = d?.rule?.all.reduce((acc, r) => {
        return {
          ...acc,
          [r.fact]: r.value,
        };
      }, {});

      return {
        ...d,
        rule: {
          event: d?.rule?.event,
        },
        ...newProps,
      };
    });
    return newData;
  };

  const moveRateRule = async data => {
    setLoading(true);
    let response = null;
    const requestData = preProcessRequestData(data);
    response = await dataProvider.put(updateApiUrl(data.id), {
      ...requestData,
    });
    if (response?.ok) {
      snackbar.showMessage(
        formatMessage({
          id: 'LABORMATRIXTABLE_UPDATE_SUCCESS',
          defaultMessage: 'Labor Matrix rule was updated successfully',
        }),
      );
    } else {
      snackbar.showMessage({
        type: 'error',
        data: formatMessage({
          id: 'LABORMATRIXTABLE_UPDATE_FAILURE',
          defaultMessage: 'Error: Could not save the labor matrix table',
        }),
      });
    }
    setLoading(false);
  };

  const setSelected = (id, priority = null) => {
    setSelectedEditId(id);
    setSelectedPriority(priority);
  };

  const listProps = {
    columnPrimaryId: 'id',
    storeName: 'LaborMatrixTable',
    dataProvider,
    schema: listSchema,
    initialState: {
      showColumnFilters: false,
    },
    downloadApiUrl: null,
    listApiUrl,
    accessorFunctions: {
      actions,
      client,
      industry,
      brand,
      productLine,
      model,
      serviceSite,
      serviceType,
      repairSeverity,
      repairCode,
      laborRate,
      travelRate,
      effectiveDate,
      endDate,
    },
    accessorFunctionsAdditional: {
      rulesFormatter,
      setSelected,
      setSelectedDeleteId,
      moveRateRule,
    },
    sortBy: 'priority',
    sortOrder: 'DESC',
    responseFormatter: data => preProcessListResponseData(data),
  };

  if (loading) {
    return <LoadingEvent>...processing</LoadingEvent>;
  }

  return (
    <Box ref={ref}>
      <Box sx={{ textAlign: 'right' }}>
        <PermissionButton
          permissionName="CAN_CREATE_EDIT_MAINTENANCE_PAGES"
          sx={{ mb: 2 }}
          size="small"
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setSelected(-1, null)}>
          Add Row
        </PermissionButton>
      </Box>
      <Box>
        <CRUDComponent
          key={tableRenderId}
          type={CRUD_COMPONENT_TYPE.LIST}
          listProps={listProps}
        />
      </Box>

      {selectedDeleteId !== null && (
        <LaborMatrixRateRuleDeleteDialog
          open={selectedDeleteId}
          onClose={() => {
            setSelectedDeleteId(null);
            setTableRenderId(nanoid());
          }}
          id={selectedDeleteId}
          parentId={laborMatrixId}
        />
      )}

      {selectedEditId !== null && (
        <LaborMatrixRateRuleEditDialog
          open={selectedEditId}
          onClose={() => {
            setSelected(null, null);
            setTableRenderId(nanoid());
          }}
          id={selectedEditId}
          priority={selectedPriority}
          parentId={laborMatrixId}
        />
      )}
    </Box>
  );
});

export default LaborMatrixTable;
